<template>
  <div
    class="tabletLandscape:top-5 tabletLandscape:bottom-auto tabletLandscape:left-auto tabletLandscape:translate-x-0 tabletLandscape:right-5 fixed bottom-20 left-1/2 z-100 flex -translate-x-1/2 flex-col gap-2.5"
  >
    <!-- notifications -->
    <TransitionGroup name="slide-pos">
      <template
        v-for="[notificationId, openNotification] in openNotifications"
        :key="notificationId"
      >
        <div v-if="bannerPropsFor(openNotification)" data-cy="notificationToast">
          <GenericPopUpBanner
            v-bind="bannerPropsFor(openNotification)!"
            :auto-close-ms="TOAST_TIMEOUT_MS"
            @on-close="dismiss(notificationId)"
          />
        </div>
      </template>
    </TransitionGroup>
    <div
      class="hidden"
      data-cy="activityPointSocket"
      :data-socket-connected="activityPointSocketStore.isConnected"
    />
    <!-- activityPoints -->
    <TransitionGroup name="slide-pos">
      <div
        v-for="[trackingId, openAlert] in openActivityPointAlerts"
        :key="trackingId"
        data-cy="activityPointToast"
      >
        <GenericPopUpBanner
          icon="icon-[mingcute--flag-4-fill]"
          :headline="activityPointHeadline(openAlert)"
          :auto-close-ms="TOAST_TIMEOUT_MS"
          @on-close="openActivityPointAlerts.delete(trackingId)"
        >
          <ExperienceBar
            bg-color="bg-tm-gray-300"
            :has-leveled-up="hasLeveledUp(openAlert)"
            :current-level="getCurrentActivityPointLevel(openAlert.totalPoints)"
            :previous-level="getPreviousActivityPointLevel(openAlert.totalPoints) ?? undefined"
            :current-points="openAlert.totalPoints"
            :latest-tracking="openAlert.tracking"
          />
        </GenericPopUpBanner>
      </div>
    </TransitionGroup>

    <!-- toasts -->
    <TransitionGroup name="slide-pos">
      <div v-for="toast in toastStore.toasts" :key="toast.id" data-cy="toast-elem">
        <GenericPopUpBanner
          v-bind="toastBannerProps(toast)"
          @on-close="toastStore.dismissToast(toast.id)"
        />
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup lang="ts">
import { useNotificationBanner } from '@components/Notifications/useNotificationBanner';
import { useActivityPointSocketStore } from '@stores/activityPointSocketStore';
import { useNotificationSocketStore } from '@stores/notificationSocketStore';
import { useQueryClient } from '@tanstack/vue-query';
import { activityPointsKeys } from '@therame/api-client';
import {
  ActivityPointAlert,
  getCurrentActivityPointLevel,
  getNotificationContent,
  getPreviousActivityPointLevel,
  NotificationFullSizeDto,
} from '@therame/shared';
import { GenericPopUpBanner } from '@therame/ui';
import { ExperienceBar } from '@therame/ui-domain';
import { onMounted, ref } from 'vue';
import { useToastStore } from '@stores/toastStore';
import { useToastBanner } from './useToastBanner';

const toastStore = useToastStore();
const { toastBannerProps } = useToastBanner();

/** The banner runs the countdown itself and closes back through its onClose. */
const TOAST_TIMEOUT_MS = 5000;

const openNotifications = ref<Map<string, NotificationFullSizeDto>>(
  new Map<string, NotificationFullSizeDto>(),
);
const notificationSocketStore = useNotificationSocketStore();

const dismiss = (notificationId: string) => {
  openNotifications.value.delete(notificationId);
};

// Auf dem transienten Toast wird der Invite nach erfolgreichem Zu-/Absagen entfernt;
// die separate Erfolgs-Benachrichtigung übernimmt die Bestätigung.
const { bannerPropsFor } = useNotificationBanner({ onActed: dismiss });

const activityPointSocketStore = useActivityPointSocketStore();
const queryClient = useQueryClient();
const openActivityPointAlerts = ref<Map<string, ActivityPointAlert>>(
  new Map<string, ActivityPointAlert>(),
);

const hasLeveledUp = (alert: ActivityPointAlert): boolean => {
  const currentLevel = getCurrentActivityPointLevel(alert.totalPoints);
  const pointsBeforeExpGain = alert.totalPoints - alert.tracking.points;
  return pointsBeforeExpGain < currentLevel.minPoints;
};

const activityPointHeadline = (alert: ActivityPointAlert): string => {
  if (hasLeveledUp(alert)) return 'Stufenaufstieg!';
  const level = getCurrentActivityPointLevel(alert.totalPoints);
  return `Level #${level.lvl}: ${level.name}`;
};

onMounted(() => {
  notificationSocketStore.notificationTrigger.subscribe((event) => {
    const notification = event.props.notification;
    // Nur Typen anzeigen, die als Banner darstellbar sind.
    if (!notification || !getNotificationContent(notification)) return;
    const notificationId = `${notification.id}`;
    openNotifications.value.set(notificationId, notification);
  });

  activityPointSocketStore.activityPointTrigger.subscribe((event) => {
    const activityPointAlert = event.props.activityPointAlert;
    if (!activityPointAlert) return;
    queryClient.invalidateQueries({ queryKey: activityPointsKeys.all() });
    const trackingId = activityPointAlert.tracking.id;
    openActivityPointAlerts.value.set(trackingId, activityPointAlert);
  });
});
</script>

<style scoped>
/* Banner gleitet aus der Richtung seiner Position ein und dorthin wieder aus.
   Mobil sitzt der Container unten -> von unten; ab tabletLandscape (1024px)
   oben rechts -> von oben. */
.slide-pos-enter-active,
.slide-pos-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.slide-pos-enter-from,
.slide-pos-leave-to {
  opacity: 0;
  transform: translateY(1.5rem);
}

@media (min-width: 64rem) {
  .slide-pos-enter-from,
  .slide-pos-leave-to {
    transform: translateY(-1.5rem);
  }
}

/* Verbleibende Banner rutschen sanft an ihre neue Position, wenn eines geht. */
.slide-pos-move {
  transition: transform 0.25s ease;
}
</style>
